// src/pages/QuotesPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatRut, formatCurrency } from '../utils/formatters';
import { quoteService } from '../services/quoteService';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

const QuotesPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // --- PERMISOS ---
    const userPermissions = user?.permissions || {};
    const isSuperUser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin' || isSuperUser;
    const canCreate = isAdmin || userPermissions.quotes?.create;
    const canEdit = isAdmin || userPermissions.quotes?.edit;
    const canDelete = isAdmin || userPermissions.quotes?.delete;

    useEffect(() => {
        const fetchQuotes = async () => {
            try {
                const data = await quoteService.getAll();
                setQuotes(data);
            } catch (error) {
                setNotification({ message: 'Error al cargar cotizaciones.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchQuotes();
    }, [setNotification]);

    const handleDeleteQuote = async (e, quote) => {
        e.stopPropagation();
        if (!window.confirm(`¿Estás seguro de eliminar la cotización ${quote.id}?`)) return;
        
        try {
            await quoteService.deleteQuote(quote.id, quote.fotos);
            setQuotes(prev => prev.filter(q => q.id !== quote.id));
            setNotification({ message: 'Cotización eliminada correctamente.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al eliminar.', type: 'error' });
        }
    };

    const filteredQuotes = quotes.filter(quote =>
        (quote.id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (quote.clientName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (quote.clientId?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const formatDate = (timestamp) => {
        if (!timestamp?.seconds) return 'N/A';
        return new Date(timestamp.seconds * 1000).toLocaleDateString('es-CL');
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            'Pendiente': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
            'Aprobada': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
            'Rechazada': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        };
        return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full transition-colors ${colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>{status}</span>;
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
            <div className="flex items-center mb-4">
                <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold mr-4 transition-colors">&larr; Volver</button>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Gestión de Cotizaciones</h1>
            </div>
            <p className="mb-6 text-gray-600 dark:text-gray-400 transition-colors">Crea, busca y gestiona todas tus cotizaciones.</p>
            
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                {canCreate && (
                    <button onClick={() => setPage('quoteForm')} className="w-full md:w-auto bg-blue-600 dark:bg-blue-700 text-white px-5 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors">+ Nueva Cotización</button>
                )}
                <input type="text" placeholder="Buscar por N°, RUT o Cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="px-4 py-2 border dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg w-full md:w-1/3 focus:ring-2 focus:ring-blue-500 outline-none transition-colors"/>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto transition-colors">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                    <thead className="bg-gray-100 dark:bg-gray-900 transition-colors">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">N° Cotización</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Fecha</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Estado</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                        {loading ? (
                            <tr><td colSpan="6" className="text-center py-10 text-gray-500 dark:text-gray-400">Cargando...</td></tr>
                        ) : (
                            filteredQuotes.map(quote => (
                                <tr key={quote.id} onClick={() => setPage('viewQuote', quote.id)} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors text-gray-900 dark:text-gray-300">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600 dark:text-blue-400">{quote.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(quote.createdAt)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900 dark:text-gray-100">{quote.clientName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(quote.total)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <StatusBadge status={quote.status} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="flex items-center justify-center gap-3">
                                            {canEdit && (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setPage('editQuote', quote.id); }} 
                                                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors" 
                                                    title="Editar"
                                                >
                                                    <PencilSquareIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button 
                                                    onClick={(e) => handleDeleteQuote(e, quote)} 
                                                    className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors" 
                                                    title="Eliminar"
                                                >
                                                    <TrashIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                 { !loading && filteredQuotes.length === 0 && <p className="text-center py-10 text-gray-500 dark:text-gray-400 transition-colors">Aún no hay cotizaciones creadas.</p>}
            </div>
        </div>
    );
};

export default QuotesPage;